import React from 'react';

import { useFeatureFlagContext } from 'Contexts/featureFlagContext';
import { useProfileContext } from 'Contexts/profileContext';
import { useTextContext } from 'Contexts/textContext';
import { Numbers, Paragraph } from 'DesignLibrary/atoms';
import Plan from 'Models/plan';
import { SplitNumbers } from 'Shared/SplitNumbers';
import { toDollars } from 'Utils/helpers';

// Helpers
import {
  formatDeductibleOrOop,
  renderDeductible,
  renderOopMax,
  renderOutNetworkDeductible,
  renderOutNetworkOopMax,
} from './_shared';
import { CompareTable } from '../_shared/CompareTable';
import { CompareTableRow } from '../_shared/CompareTableRow';
import { NetworkTier } from '../types';
import RenderProvidersComponent from './_shared/RenderProvidersComponent';

interface DeductiblesLimitsProps {
  plans: Plan[];
  showCompareRows: boolean;
  getSelectedTier: (plan: Plan) => NetworkTier;
}

const renderTierDeductible = (plan: Plan, tier: NetworkTier, noOonNode: React.ReactElement) => {
  if (tier === 'out_of_network' && !plan.hasOonCoverage()) return noOonNode;
  const { medical_and_drug_deductible, medical_deductible, drug_deductible } =
    plan.getPlanDetailsForNetworkTier(tier);

  if (medical_and_drug_deductible !== null) {
    return <Numbers size="medium">{toDollars(medical_and_drug_deductible)}</Numbers>;
  }

  if (medical_deductible !== null || drug_deductible !== null) {
    return (
      <SplitNumbers
        values={[
          formatDeductibleOrOop(medical_deductible, true),
          formatDeductibleOrOop(drug_deductible, true),
        ]}
        identifiers={['med-deductible', 'drug-deductible']}
      />
    );
  }

  return formatDeductibleOrOop(null);
};

const renderTierOopMax = (plan: Plan, tier: NetworkTier, noOonNode: React.ReactElement) => {
  if (tier === 'out_of_network' && !plan.hasOonCoverage()) return noOonNode;
  const { medical_and_drug_oop_max, medical_oop_max, drug_oop_max } = plan.getPlanDetailsForNetworkTier(tier);

  if (medical_and_drug_oop_max !== null) {
    return <Numbers size="medium">{toDollars(medical_and_drug_oop_max)}</Numbers>;
  }

  if (medical_oop_max !== null || drug_oop_max !== null) {
    return (
      <SplitNumbers
        values={[formatDeductibleOrOop(medical_oop_max, true), formatDeductibleOrOop(drug_oop_max, true)]}
        identifiers={['med-oop-limit', 'drug-oop-limit']}
      />
    );
  }

  return formatDeductibleOrOop(null);
};

export const DeductiblesLimits = ({ plans, showCompareRows, getSelectedTier }: DeductiblesLimitsProps) => {
  const { retrieveContentfulData } = useTextContext();
  const { selectedPublicationInfo } = useProfileContext();
  const { is_network_tier_toggle_enabled } = useFeatureFlagContext();

  if (!plans.length) {
    return null; // Avoid rendering if no plans exist
  }

  const docFinderEnabled = selectedPublicationInfo?.publication?.docfinder_enabled;

  const firstPlan = plans[0] || null;
  const secondPlan = plans[1] || null;
  const thirdPlan = plans[2] || null;

  // HEADER AND ITEM TEXT
  const inNetworkTitle = retrieveContentfulData<string>(
    'health_section.compare_plan_text.compareModal.inNetwork',
  );
  const deductiblesAndMaximumsTitle =
    retrieveContentfulData<string>('health_section.compare_plan_text.compareModal.deductiblesAndMaximums') ||
    'Deductibles and Maximums';
  const outOfNetworkTitle = retrieveContentfulData<string>(
    'health_section.compare_plan_text.compareModal.outOfNetwork',
  );
  const deductibleTitle = retrieveContentfulData<string>(
    'health_section.compare_plan_text.compareModal.deductible',
  );
  const oopLimitTitle = retrieveContentfulData<string>(
    'health_section.compare_plan_text.compareModal.oopLimit',
  );

  const doctorsProvidersTitle = retrieveContentfulData<string>(
    'health_details_section.plan_details.doctors_providers_card_title',
  );

  // ITEM TOOLTIPS
  const inNetworkDeductibleTooltip = retrieveContentfulData<string>('tool_tips.in_network_deductible.text');
  const inNetworkOopTooltip = retrieveContentfulData<string>('tool_tips.in_network_out_of_pocket_limit.text');
  const outNetworkDeductibleTooltip = retrieveContentfulData<string>(
    'tool_tips.out_of_network_deductible.text',
  );
  const outNetworkOopTooltip = retrieveContentfulData<string>(
    'tool_tips.out_of_network_out_of_pocket_limit.text',
  );

  if (!firstPlan) {
    return null; // Ensure we don't proceed if the first plan is missing
  }

  const firstTier = firstPlan ? getSelectedTier(firstPlan) : 'in_network';
  const secondTier = secondPlan ? getSelectedTier(secondPlan) : 'in_network';
  const thirdTier = thirdPlan ? getSelectedTier(thirdPlan) : 'in_network';

  const noOonCoverageText =
    retrieveContentfulData<string>('health_section.compare_plan_text.compareModal.noOonCoverage') ||
    'No out-of-network coverage';
  const noOonNode = <Paragraph size="small">{noOonCoverageText}</Paragraph>;

  const renderOonDed = (plan: Plan) => renderOutNetworkDeductible(plan);
  const renderOonOop = (plan: Plan) => renderOutNetworkOopMax(plan);

  const getInnDeductible = (plan: Plan, tier: NetworkTier) => {
    if (!is_network_tier_toggle_enabled) return renderDeductible(plan);
    return renderTierDeductible(plan, tier, noOonNode);
  };

  const getInnOopMax = (plan: Plan, tier: NetworkTier) => {
    if (!is_network_tier_toggle_enabled) return renderOopMax(plan);
    return renderTierOopMax(plan, tier, noOonNode);
  };

  return (
    <>
      <CompareTable
        tableHeader={is_network_tier_toggle_enabled ? deductiblesAndMaximumsTitle : inNetworkTitle}
      >
        <CompareTableRow
          rowHeader={deductibleTitle}
          rowHeaderTooltip={inNetworkDeductibleTooltip}
          columnOne={firstPlan && getInnDeductible(firstPlan, firstTier)}
          columnTwo={secondPlan && getInnDeductible(secondPlan, secondTier)}
          columnThree={thirdPlan && getInnDeductible(thirdPlan, thirdTier)}
          keyHint="inn-deductible"
        />

        <CompareTableRow
          rowHeader={oopLimitTitle}
          rowHeaderTooltip={inNetworkOopTooltip}
          columnOne={firstPlan && getInnOopMax(firstPlan, firstTier)}
          columnTwo={secondPlan && getInnOopMax(secondPlan, secondTier)}
          columnThree={thirdPlan && getInnOopMax(thirdPlan, thirdTier)}
          keyHint="inn-moop"
        />

        {/* Doctors & Providers */}
        {showCompareRows && docFinderEnabled && (
          <div data-testid="compare-in-provider">
            <CompareTableRow
              rowHeader={doctorsProvidersTitle}
              columnOne={
                firstPlan &&
                (is_network_tier_toggle_enabled && firstTier === 'out_of_network' ? (
                  <Paragraph size="small">N/A</Paragraph>
                ) : (
                  <RenderProvidersComponent plan={firstPlan} inNetwork />
                ))
              }
              columnTwo={
                secondPlan &&
                (is_network_tier_toggle_enabled && secondTier === 'out_of_network' ? (
                  <Paragraph size="small">N/A</Paragraph>
                ) : (
                  <RenderProvidersComponent plan={secondPlan} inNetwork />
                ))
              }
              columnThree={
                thirdPlan &&
                (is_network_tier_toggle_enabled && thirdTier === 'out_of_network' ? (
                  <Paragraph size="small">N/A</Paragraph>
                ) : (
                  <RenderProvidersComponent plan={thirdPlan} inNetwork />
                ))
              }
              hideTooltip
              keyHint="inn-doctors-providers"
            />
          </div>
        )}
      </CompareTable>

      {/* OUT-OF-NETWORK — hidden when toggle flag is ON (user selects OON tier via dropdown) */}
      {!is_network_tier_toggle_enabled && (
        <CompareTable tableHeader={outOfNetworkTitle}>
          {/* OUT-OF-NETWORK DEDUCTIBLE */}
          <CompareTableRow
            rowHeader={deductibleTitle}
            rowHeaderTooltip={outNetworkDeductibleTooltip}
            columnOne={firstPlan && renderOonDed(firstPlan)}
            columnTwo={secondPlan && renderOonDed(secondPlan)}
            columnThree={thirdPlan && renderOonDed(thirdPlan)}
            keyHint="oon-deductible"
          />

          {/* OUT-OF-NETWORK OOP MAX */}
          <CompareTableRow
            rowHeader={oopLimitTitle}
            rowHeaderTooltip={outNetworkOopTooltip}
            columnOne={firstPlan && renderOonOop(firstPlan)}
            columnTwo={secondPlan && renderOonOop(secondPlan)}
            columnThree={thirdPlan && renderOonOop(thirdPlan)}
            keyHint="oon-moop"
          />

          {/* Doctors & Providers */}
          {showCompareRows && docFinderEnabled && (
            <div data-testid="compare-out-provider">
              <CompareTableRow
                rowHeader={doctorsProvidersTitle}
                columnOne={firstPlan && <RenderProvidersComponent plan={firstPlan} inNetwork={false} />}
                columnTwo={secondPlan && <RenderProvidersComponent plan={secondPlan} inNetwork={false} />}
                columnThree={thirdPlan && <RenderProvidersComponent plan={thirdPlan} inNetwork={false} />}
                hideTooltip
                keyHint="oon-doctors-providers"
              />
            </div>
          )}
        </CompareTable>
      )}
    </>
  );
};
